import type {ComputedRef} from 'vue';
import {ref} from 'vue';
import {businessReferralsEndpoints, submissionApi} from '@registry';
import {useRoute} from '@typed-router';
import {compareAsc} from 'date-fns';
import type {FetchError} from 'ofetch';
import {defineStore, storeToRefs} from 'pinia';
import {computed, showError, useAuth, useLogger, useProjectStateProps, useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {
    ReferralResource,
    ReferralSubmissionForReferrerResponse,
    ReferredProjectDeliverableResource,
    ReferredProjectResource,
    UpdateReferralRewardPercentageRequest,
    UpdateReferrerRequest,
} from '@api';

export enum ProjectType {
    CostPlus = 'COST_PLUS',
    FixedPrice = 'FIXED_PRICE',
}

export enum ReferralStatus {
    Evaluated = 'EVALUATED',
    StaffingInProgress = 'STAFFING_IN_PROGRESS',
    Cancelled = 'CANCELLED',
    Ongoing = 'ONGOING',
    Expired = 'EXPIRED',
}

export enum ProjectStatus {
    Approved = 'APPROVED',
    OngoingProject = 'ONGOING_PROJECT',
    Cancelled = 'CANCELLED',
    Ended = 'ENDED',
}

export interface ReferralProjectView {
    referral: ReferralView;
    project?: ProjectView;
}

export interface ReferralView {
    opportunityTitle: string;
    companyName: string;
    submissionDate: Date;
    rewardPercentage: number;
    status: ReferralStatus;
    multipleFreelancersReferred: boolean;
    referralId: string;
    referrerEmail?: string;
    referredProjectNames: string[];
    referredProjectEarnings: number;
    currency?: string;
    clientName?: string;
    clientId?: string;
    repatriationOpportunityId?: string;
    sourcingOpportunityId?: string;
}

export interface ProjectView {
    projectId: string;
    projectTitle: string;
    earnings: number;
    type: ProjectType;
    status: ProjectStatus;
    referredProjectId?: string;
    initialReferredProjectId?: string;
    deliverables: ReferredProjectDeliverableResource[];
    currency?: string;
    freelancerName?: string;
    freelancerId?: string;
    canUpdateInternalReferralData?: boolean;
}

export const useReferralsStore = defineStore('referrals-store', () => {
    const auth = useAuth();
    const route = useRoute();
    const {getSelectedIdentity} = storeToRefs(auth);
    const {getProjectStateProps} = useProjectStateProps();
    const logger = useLogger();
    const {t} = useTranslation();

    const isClientOpsDialogVisible = ref(false);
    const referralProjectView = ref<ReferralProjectView[]>([]);
    const selectedReferralOrProject = ref<ReferralProjectView | null>(null);
    const selectedProjectIsRepeated = computed(() => selectedReferralOrProject.value?.project?.initialReferredProjectId !== undefined);
    const selectedReferralHasMultipleFreelancers = computed(() => selectedReferralOrProject.value?.referral.multipleFreelancersReferred);
    const isProjectRepeatedOrHasMultiFreelancers = computed(
        () => selectedProjectIsRepeated.value || selectedReferralOrProject.value?.referral.multipleFreelancersReferred,
    );
    const totalEarnings = computed(() => {
        return referralProjectView.value
            .filter((view) => view.project)
            .map((view) => view.project!)
            .reduce((acc, project) => acc + project.earnings, 0);
    });

    const loadingReferrals = ref<boolean>(false);
    const isOpenHowItWorksModal = ref<boolean>(false);
    const hasNoReferrals = computed(() => referralProjectView.value.length === 0);
    const initialProject: ComputedRef<ReferralProjectView | null> = computed(() => {
        const initialReferredProjectId = selectedReferralOrProject.value?.project?.initialReferredProjectId;
        if (!initialReferredProjectId) {
            return null;
        }
        return referralProjectView.value.find((referral) => referral.project?.referredProjectId === initialReferredProjectId) ?? null;
    });

    const immersionIdentityId = ref(route.query.identityId as string);
    const immersionEnabled = computed(() => immersionIdentityId.value !== undefined);

    async function fetchReferrals() {
        if (!getSelectedIdentity.value) {
            return;
        }

        try {
            loadingReferrals.value = true;
            const referrals = await businessReferralsEndpoints.findReferralsByReferrer({
                referrerIdentityId: immersionEnabled.value ? immersionIdentityId.value : getSelectedIdentity.value.id,
            });

            const submissions = await submissionApi.getSubmissionsByReferrer({
                referrerIdentityId: immersionEnabled.value ? immersionIdentityId.value : getSelectedIdentity.value.id,
            });

            const pendingToBecomeReferrals = submissions.map((submission: ReferralSubmissionForReferrerResponse) => {
                return {
                    companyName: submission.companyName,
                    submissionDate: submission.submissionDate,
                    referralId: submission.submissionId,
                    referredProjects: [],
                    earnings: 0,
                    rewardPercentage: submission.referrerRate,
                    multipleFreelancersReferred: false,
                    status: ReferralStatus.Evaluated,
                    opportunityTitle: submission.projectTitle,
                    referrerAccountId: submission.referrerAccountId,
                    referrerIdentityId: submission.referrerIdentityId,
                    isCancelled: false,
                    isExpired: false,
                } as ReferralResource;
            });

            referralProjectView.value = [...referrals, ...pendingToBecomeReferrals]
                .flatMap((referral: ReferralResource): ReferralProjectView[] => {
                    const projects = referral.referredProjects;
                    const referralProjectView: ReferralProjectView = {
                        referral: {
                            opportunityTitle: referral.opportunityTitle,
                            companyName: referral.companyName,
                            submissionDate: referral.submissionDate,
                            rewardPercentage: referral.rewardPercentage,
                            multipleFreelancersReferred: referral.multipleFreelancersReferred,
                            status: pendingToBecomeReferrals.some((submission) => submission.referralId === referral.referralId)
                                ? ReferralStatus.Evaluated
                                : referral.isCancelled
                                  ? ReferralStatus.Cancelled
                                  : referral.isExpired
                                    ? ReferralStatus.Expired
                                    : projects.length == 0
                                      ? ReferralStatus.StaffingInProgress
                                      : ReferralStatus.Ongoing,
                            referralId: referral.referralId,
                            referrerEmail: referral.referrerEmail,
                            referredProjectNames: referral.referredProjects.map((p) => p.projectTitle),
                            referredProjectEarnings: referral.referredProjects
                                .flatMap((p) => p.deliverables)
                                .reduce((acc: any, deliverable: any) => acc + (deliverable.earnings ?? 0), 0),
                            currency: referral.referredProjects.map((p) => p.currencyCode)?.[0] || '',
                            clientName: referral.client?.fullName,
                            clientId: referral.client?.identityId,
                            repatriationOpportunityId: referral.repatriationOpportunityId,
                            sourcingOpportunityId: referral.sourcingOpportunityId,
                        },
                    };
                    return projects.length === 0
                        ? [referralProjectView]
                        : projects.map((referredProject: ReferredProjectResource): ReferralProjectView => {
                              return {
                                  ...referralProjectView,
                                  project: {
                                      projectId: referredProject.projectId,
                                      projectTitle: referredProject.projectTitle,
                                      referredProjectId: referredProject.id,
                                      earnings: referredProject.deliverables.reduce(
                                          (acc: any, deliverable: any) => acc + (deliverable.earnings ?? 0),
                                          0,
                                      ),
                                      status: referredProject.status as ProjectStatus,
                                      initialReferredProjectId: referredProject.initialReferredProjectId,
                                      deliverables: referredProject.deliverables.map(
                                          (deliverable: ReferredProjectDeliverableResource): ReferredProjectDeliverableResource => ({
                                              ...deliverable,
                                              earnings: deliverable.earnings ?? 0,
                                          }),
                                      ),
                                      currency: referredProject.currencyCode,
                                      type: referredProject.projectType as ProjectType,
                                      freelancerName: referredProject.freelancer.fullName,
                                      freelancerId: referredProject.freelancer.accountId,
                                      canUpdateInternalReferralData: referredProject.canUpdateInternalReferralData,
                                  },
                              };
                          });
                })
                .sort((a: ReferralProjectView, b: ReferralProjectView) => {
                    const dateComp = compareAsc(b.referral.submissionDate, a.referral.submissionDate);
                    return dateComp !== 0 ? dateComp : a.referral.referralId.localeCompare(b.referral.referralId);
                });
        } catch (e) {
            logger.error(e);
            const error = e as FetchError;
            showError({statusCode: error.statusCode, stack: error.message});
        } finally {
            loadingReferrals.value = false;
        }
    }

    const referrals = computed<ReferralView[]>(() => {
        const seen = new Set<string>();
        return referralProjectView.value
            .filter((v) => {
                if (seen.has(v.referral.referralId)) return false;
                seen.add(v.referral.referralId);
                return true;
            })
            .map((v) => v.referral);
    });

    function projectsForReferral(referralId: string): ProjectView[] {
        return referralProjectView.value.filter((v) => v.referral.referralId === referralId && !!v.project).map((v) => v.project!);
    }

    function referralById(referralId: string): ReferralView | undefined {
        return referralProjectView.value.find((v) => v.referral.referralId === referralId)?.referral;
    }

    const projectStateProps = computed(
        () => selectedReferralOrProject.value?.project?.status && getProjectStateProps(selectedReferralOrProject.value.project.status),
    );

    const isRecurringProject = computed(() => selectedReferralOrProject.value?.project?.type === ProjectType.CostPlus);

    const totalEarningsByCurrency = computed(() => {
        const totalForEachCurrency = referralProjectView.value
            .filter((view) => view.project)
            .map((view) => view.project!)
            .reduce((accumulator: Array<{currency: string; totalAmount: number}>, current: ProjectView) => {
                if (current.currency === undefined) {
                    return accumulator;
                }
                const existingCurrency = accumulator.find((item) => item.currency === current.currency);

                if (existingCurrency) {
                    existingCurrency.totalAmount += current.earnings;
                } else if (current.earnings > 0) {
                    accumulator.push({currency: current.currency!, totalAmount: current.earnings});
                }
                return accumulator;
            }, []);
        return totalForEachCurrency.sort((a, b) => b.totalAmount - a.totalAmount);
    });

    function openHowItWorksModal(value: boolean) {
        isOpenHowItWorksModal.value = value;
    }

    function setSelectedReferralOrProject(value: ReferralProjectView | null) {
        selectedReferralOrProject.value = value;
    }

    function setSelectedReferralOrProjectById(id: string): ReferralProjectView | null {
        selectedReferralOrProject.value =
            referralProjectView.value.find((view) => view.project?.referredProjectId === id || view.referral.referralId === id) ?? null;
        return selectedReferralOrProject.value;
    }

    // admin only actions
    async function updateRewardPercentage(referredProjectId: string, rewardRequest: UpdateReferralRewardPercentageRequest) {
        try {
            await businessReferralsEndpoints.updateReferralRewardPercentage({
                referralId: referredProjectId,
                updateReferralRewardPercentageRequest: rewardRequest,
            });
            pushVJoySnackbar({props: {message: t('business-referral.details.admin.modal.reward.notification.success'), level: 'success'}});
        } catch (e) {
            logger.error(e);
            const error = e as FetchError;
            showError({statusCode: error.statusCode, stack: error.message});
            pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
        }
    }

    async function updateReferrerEmail(referredProjectId: string, referrerRequest: UpdateReferrerRequest) {
        try {
            await businessReferralsEndpoints.updateReferrer({
                referralId: referredProjectId,
                updateReferrerRequest: referrerRequest,
            });
            pushVJoySnackbar({props: {message: t('business-referral.details.admin.modal.referrer.notification.success'), level: 'success'}});
        } catch (e: any) {
            logger.error(e);
            showError({statusCode: e.statusCode, stack: e.message});
            if (e.errorCode === 'ReferrerNotFound') {
                pushVJoySnackbar({props: {message: t('business-referral.details.admin.modal.referrer.notification.error.account'), level: 'error'}});
            } else {
                pushVJoySnackbar({props: {message: t('generic.technical.error'), level: 'error'}});
            }
        }
    }

    return {
        isClientOpsDialogVisible,
        referralProjects: readonlyState(referralProjectView),
        loadingReferrals: readonlyState(loadingReferrals),
        selectedReferralOrProject: selectedReferralOrProject,
        isOpenHowItWorksModal: readonlyState(isOpenHowItWorksModal),
        immersionIdentityId,
        immersionEnabled,
        initialProject,
        selectedProjectIsRepeated,
        selectedReferralHasMultipleFreelancers,
        totalEarnings,
        isProjectRepeatedOrHasMultiFreelancers,
        hasNoReferrals,
        totalEarningsByCurrency,
        isRecurringProject,
        projectStateProps,
        fetchReferrals,
        openHowItWorksModal,
        setSelectedReferralOrProject,
        updateRewardPercentage,
        updateReferrerEmail,
        setSelectedReferralOrProjectById,
        referrals,
        projectsForReferral,
        referralById,
    };
});
